import React, { useMemo, useState } from "react";
import { Alert, Badge, Spinner, Table } from "flowbite-react";
import {
  HiOutlineOfficeBuilding,
  HiOutlineUsers,
  HiOutlineCurrencyDollar,
  HiOutlineExclamation,
  HiOutlineArrowRight,
  HiOutlineTrendingUp,
  HiOutlineTrendingDown,
  HiOutlineLightningBolt,
  HiOutlineRefresh,
} from "react-icons/hi";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import moment from "moment";
import Chart from "react-apexcharts";
import axios from "../config/axiosInstance";
import { ADMIN } from "../config/apiConfig";

const formatCurrency = (value) =>
  (Number(value) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

const formatNumber = (value) => (Number(value) || 0).toLocaleString("pt-BR");

const formatMonth = (month) => {
  const parsed = moment(month, "YYYY-MM");
  return parsed.isValid() ? parsed.format("MMM/YY") : month;
};

const STATUS_META = {
  "-1": { label: "Inativa", color: "gray" },
  0: { label: "Bloqueada", color: "failure" },
  1: { label: "Ativa", color: "success" },
};

const PERMISSION_LABELS = {
  POWER_USER: "Power User",
  ADMIN: "Administrador",
  CLIENTE: "Cliente",
  REPRESENTANTE: "Representante",
};

/* ------------------------------------------------------------------ *
 * Blocos reutilizáveis
 * ------------------------------------------------------------------ */
const Delta = ({ value }) => {
  if (value === undefined || value === null) return null;
  const positive = value >= 0;
  const Icon = positive ? HiOutlineTrendingUp : HiOutlineTrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${
        positive ? "text-emerald-600" : "text-rose-600"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {positive ? "+" : ""}
      {value}%
    </span>
  );
};

const MetricCard = ({ icon: Icon, label, value, hint, delta, tone = "slate" }) => {
  const tones = {
    slate: "bg-white text-slate-900 border-slate-200",
    dark: "bg-slate-900 text-white border-slate-900",
    emerald: "bg-emerald-600 text-white border-emerald-600",
    amber: "bg-amber-500 text-white border-amber-500",
  };
  const muted = tone === "slate" ? "text-slate-400" : "text-white/70";
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border px-4 py-3 shadow-sm ${tones[tone]}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className={`text-[10px] uppercase tracking-[0.25em] ${muted}`}>
          {label}
        </p>
        {Icon && <Icon className={`h-4 w-4 ${muted}`} />}
      </div>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      <div className="flex items-center gap-2">
        {hint && <span className={`text-[11px] ${muted}`}>{hint}</span>}
        <Delta value={delta} />
      </div>
    </div>
  );
};

const Panel = ({ title, subtitle, action, children, className = "" }) => (
  <section
    className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}
  >
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="text-[11px] text-slate-400">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

/** Barra de proporção usada nas distribuições e na adoção de recursos. */
const ShareBar = ({ label, count, percent, color = "bg-slate-900" }) => (
  <div className="space-y-1">
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="truncate text-slate-600">{label}</span>
      <span className="shrink-0 font-medium text-slate-900">
        {formatNumber(count)}
        <span className="ml-1 text-[10px] text-slate-400">{percent}%</span>
      </span>
    </div>
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div
        className={`h-full rounded-full ${color}`}
        style={{ width: `${Math.min(100, percent)}%` }}
      />
    </div>
  </div>
);

/* ------------------------------------------------------------------ *
 * Dashboard
 * ------------------------------------------------------------------ */
export default function Dashboard() {
  const [activityDays, setActivityDays] = useState(30);
  const [months, setMonths] = useState(12);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ["admin-dashboard", months, activityDays],
    async () => {
      const response = await axios.get(ADMIN.GET_DASHBOARD, {
        params: { months, activityDays, topLimit: 10 },
      });
      return response.data?.data || null;
    },
    { staleTime: 2 * 60 * 1000, keepPreviousData: true }
  );

  const totals = data?.totals;
  const revenue = data?.revenue;
  const growthMetrics = data?.growthMetrics;
  const timelines = data?.timelines;
  const distribution = data?.distribution;
  const adoption = data?.adoption;
  const rankings = data?.rankings;
  const recent = data?.recent;

  const growthChart = useMemo(() => {
    if (!timelines) return null;
    return {
      series: [
        {
          name: "Empresas",
          data: timelines.companies.map((point) => point.count),
        },
        { name: "Usuários", data: timelines.users.map((point) => point.count) },
        { name: "Leads", data: timelines.leads.map((point) => point.count) },
      ],
      options: {
        chart: { type: "area", toolbar: { show: false }, fontFamily: "inherit" },
        colors: ["#0f172a", "#f97316", "#38bdf8"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0 } },
        xaxis: {
          categories: timelines.companies.map((point) => formatMonth(point.month)),
          labels: { style: { fontSize: "10px" } },
        },
        yaxis: { labels: { style: { fontSize: "10px" } } },
        legend: { position: "top", fontSize: "11px" },
        grid: { borderColor: "#f1f5f9" },
        tooltip: { shared: true },
      },
    };
  }, [timelines]);

  const pulseChart = useMemo(() => {
    if (!timelines) return null;
    return {
      series: [
        {
          name: "Ações na plataforma",
          data: timelines.activityByDay.map((point) => point.count),
        },
      ],
      options: {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
        colors: ["#0f172a"],
        dataLabels: { enabled: false },
        plotOptions: { bar: { columnWidth: "60%", borderRadius: 2 } },
        xaxis: {
          categories: timelines.activityByDay.map((point) =>
            moment(point.day).format("DD/MM")
          ),
          labels: { style: { fontSize: "9px" }, rotate: 0, hideOverlappingLabels: true },
          tickAmount: 10,
        },
        yaxis: { labels: { style: { fontSize: "10px" } } },
        grid: { borderColor: "#f1f5f9" },
      },
    };
  }, [timelines]);

  const revenueChart = useMemo(() => {
    if (!revenue) return null;
    return {
      series: [
        {
          name: "Receita aprovada",
          data: revenue.timeline.map((point) => Number(point.total.toFixed(2))),
        },
      ],
      options: {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
        colors: ["#059669"],
        dataLabels: { enabled: false },
        plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
        xaxis: {
          categories: revenue.timeline.map((point) => formatMonth(point.month)),
          labels: { style: { fontSize: "10px" } },
        },
        yaxis: {
          labels: {
            style: { fontSize: "10px" },
            formatter: (value) => formatCurrency(value),
          },
        },
        grid: { borderColor: "#f1f5f9" },
        tooltip: { y: { formatter: (value) => formatCurrency(value) } },
      },
    };
  }, [revenue]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-24 text-sm text-slate-500">
        <Spinner color="info" />
        Montando o panorama da operação...
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert color="failure">
        <span className="font-semibold">Não foi possível carregar o dashboard.</span>{" "}
        Tente novamente mais tarde.
      </Alert>
    );
  }

  return (
    <div className="space-y-5">
      {/* Cabeçalho + janelas de análise */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
            Visão geral
          </p>
          <h1 className="text-2xl font-semibold text-slate-900">
            Painel Relinq
          </h1>
          <p className="text-xs text-slate-400">
            Atualizado em {moment(data.generatedAt).format("DD/MM/YYYY HH:mm")}
            {isFetching ? " · atualizando..." : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-[10px] border border-slate-200 px-3 py-2 text-[13px] text-slate-700 focus:border-slate-400 focus:ring-0"
            value={activityDays}
            onChange={(event) => setActivityDays(Number(event.target.value))}
          >
            {[7, 15, 30, 60, 90].map((days) => (
              <option key={days} value={days}>
                Atividade: {days} dias
              </option>
            ))}
          </select>
          <select
            className="rounded-[10px] border border-slate-200 px-3 py-2 text-[13px] text-slate-700 focus:border-slate-400 focus:ring-0"
            value={months}
            onChange={(event) => setMonths(Number(event.target.value))}
          >
            {[6, 12, 18, 24].map((value) => (
              <option key={value} value={value}>
                Histórico: {value} meses
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-1 rounded-[10px] border border-slate-200 px-3 py-2 text-[13px] text-slate-600 transition hover:bg-slate-50"
          >
            <HiOutlineRefresh className="h-4 w-4" />
            Atualizar
          </button>
        </div>
      </header>

      {/* Linha 1 — números que importam */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-6">
        <MetricCard
          tone="dark"
          icon={HiOutlineOfficeBuilding}
          label="Empresas"
          value={formatNumber(totals.companies)}
          hint={`${totals.companiesActive} ativas`}
        />
        <MetricCard
          icon={HiOutlineUsers}
          label="Usuários"
          value={formatNumber(totals.users)}
          hint={`${totals.activeUsers} ativos`}
          delta={growthMetrics.usersGrowth}
        />
        <MetricCard
          tone="emerald"
          icon={HiOutlineCurrencyDollar}
          label="MRR estimado"
          value={formatCurrency(revenue.mrr)}
          hint={`ARR ${formatCurrency(revenue.arr)}`}
        />
        <MetricCard
          icon={HiOutlineCurrencyDollar}
          label="Recebido no mês"
          value={formatCurrency(revenue.approvedThisMonth)}
          hint={`ARPA ${formatCurrency(revenue.arpa)}`}
          delta={revenue.approvedGrowth}
        />
        <MetricCard
          icon={HiOutlineLightningBolt}
          label={`Leads (${activityDays}d)`}
          value={formatNumber(growthMetrics.leadsInWindow)}
          hint={`${formatNumber(totals.leadsAllTime)} no total`}
          delta={growthMetrics.leadsGrowth}
        />
        <MetricCard
          tone="amber"
          icon={HiOutlineExclamation}
          label="Vencendo/vencido"
          value={formatNumber(
            distribution.expiration.expired + distribution.expiration.next7
          )}
          hint={`${distribution.expiration.expired} expiradas · ${distribution.expiration.next7} em 7d`}
        />
      </div>

      {/* Linha 2 — crescimento e pulso */}
      <div className="grid gap-4 xl:grid-cols-3">
        <Panel
          className="xl:col-span-2"
          title="Crescimento"
          subtitle={`Novos cadastros e leads nos últimos ${months} meses`}
        >
          {growthChart && (
            <Chart
              options={growthChart.options}
              series={growthChart.series}
              type="area"
              height={260}
            />
          )}
        </Panel>

        <Panel
          title="Distribuição da base"
          subtitle={`${totals.paidCompanies} contratadas · ${totals.trialCompanies} em teste`}
        >
          <div className="space-y-3">
            {distribution.plans.slice(0, 6).map((plan) => (
              <ShareBar
                key={plan.name}
                label={plan.name}
                count={plan.count}
                percent={plan.percent}
              />
            ))}
          </div>
          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="mb-2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
              Adoção de recursos
            </p>
            <div className="space-y-2.5">
              {[
                ["WhatsApp conectado", adoption.whatsapp],
                ["Agente de IA", adoption.aiAgent],
                ["Telefonia", adoption.voip],
                ["Onboarding concluído", adoption.onboarding],
                ["Com funis", adoption.withFunnels],
              ].map(([label, metric]) => (
                <ShareBar
                  key={label}
                  label={label}
                  count={metric.count}
                  percent={metric.percent}
                  color="bg-orange-500"
                />
              ))}
            </div>
          </div>
        </Panel>
      </div>

      {/* Linha 3 — receita + pulso diário */}
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          title="Receita aprovada"
          subtitle="Pagamentos confirmados por mês"
        >
          {revenueChart && (
            <Chart
              options={revenueChart.options}
              series={revenueChart.series}
              type="bar"
              height={240}
            />
          )}
          {revenue.byPlan.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
              {revenue.byPlan.map((item) => (
                <Badge key={item.plan} color="gray" size="xs">
                  {item.plan}: {formatCurrency(item.mrr)}/mês
                </Badge>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Pulso da plataforma"
          subtitle={`Ações registradas por dia (${activityDays} dias) · ${formatNumber(
            growthMetrics.activitiesInWindow
          )} no período`}
          action={<Delta value={growthMetrics.activitiesGrowth} />}
        >
          {pulseChart && (
            <Chart
              options={pulseChart.options}
              series={pulseChart.series}
              type="bar"
              height={240}
            />
          )}
        </Panel>
      </div>

      {/* Linha 4 — quem mais usa e quem está sumido */}
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel
          title="Clientes que mais usam"
          subtitle={`Score combina leads, cards, orçamentos, ações e usuários ativos (${activityDays} dias)`}
          action={
            <Link
              to="/companies"
              className="text-[11px] font-medium text-orange-600 hover:underline"
            >
              Ver todas
            </Link>
          }
        >
          <div className="overflow-x-auto">
            <Table hoverable>
              <Table.Head className="bg-slate-50 text-slate-500">
                {["#", "Cliente", "Leads", "Cards", "Orç.", "Ações", "Usuários", "Score"].map(
                  (label) => (
                    <Table.HeadCell
                      key={label}
                      className="whitespace-nowrap px-2 py-2 text-[10px] uppercase tracking-[0.2em]"
                    >
                      {label}
                    </Table.HeadCell>
                  )
                )}
              </Table.Head>
              <Table.Body className="divide-y divide-slate-100">
                {rankings.topByUsage.map((company, index) => (
                  <Table.Row key={company.id} className="bg-white hover:bg-slate-50">
                    <Table.Cell className="px-2 py-1.5 text-xs text-slate-400">
                      {index + 1}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-1.5">
                      <Link
                        to={`/companies/${company.id}`}
                        className="text-xs font-semibold text-slate-900 hover:text-orange-600 hover:underline"
                      >
                        {company.name}
                      </Link>
                      <p className="text-[10px] text-slate-400">
                        {company.plan}
                        {company.isTrial ? " · teste" : ""}
                      </p>
                    </Table.Cell>
                    <Table.Cell className="px-2 py-1.5 text-xs">
                      {formatNumber(company.leads)}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-1.5 text-xs">
                      {formatNumber(company.cards)}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-1.5 text-xs">
                      {formatNumber(company.budgets)}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-1.5 text-xs">
                      {formatNumber(company.activities)}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-1.5 text-xs">
                      {company.activeUsers}/{company.users}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold text-slate-900">
                          {company.score}
                        </span>
                        <Delta value={company.trend} />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </Panel>

        <Panel
          title="Contratos sem uso"
          subtitle={`Clientes pagantes sem nenhuma atividade nos últimos ${activityDays} dias`}
        >
          {rankings.atRisk.length === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400">
              Nenhum cliente pagante parado no período. 🎉
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rankings.atRisk.map((company) => (
                <li key={company.id}>
                  <Link
                    to={`/companies/${company.id}`}
                    className="flex items-center justify-between gap-2 py-2 transition hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-slate-900">
                        {company.name}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {company.plan} · {company.users} usuários ·{" "}
                        {formatNumber(company.leadsAllTime)} leads no total
                      </p>
                    </div>
                    <HiOutlineArrowRight className="h-4 w-4 shrink-0 text-slate-300" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Linha 5 — últimos cadastros */}
      <div className="grid gap-4 xl:grid-cols-3">
        <Panel
          title="Novas empresas"
          subtitle={`${growthMetrics.companiesThisMonth} cadastradas neste mês`}
          action={<Delta value={growthMetrics.companiesGrowth} />}
        >
          {recent.companies.length === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400">
              Nenhum cadastro recente.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recent.companies.map((company) => {
                const statusMeta =
                  STATUS_META[String(company.status)] || {
                    label: company.status,
                    color: "gray",
                  };
                return (
                  <li key={company.id}>
                    {/* Clicável: leva direto ao detalhe do cliente */}
                    <Link
                      to={`/companies/${company.id}`}
                      className="group flex items-center justify-between gap-2 rounded-lg px-1 py-2 transition hover:bg-slate-50"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold text-slate-900 group-hover:text-orange-600">
                          {company.name}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {company.plan} ·{" "}
                          {moment(company.createdAt).format("DD/MM/YYYY")}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <Badge color={statusMeta.color} size="xs">
                          {statusMeta.label}
                        </Badge>
                        <HiOutlineArrowRight className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-orange-500" />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel
          title="Novos usuários"
          subtitle={`${growthMetrics.usersThisMonth} cadastrados neste mês`}
          action={
            <Link
              to="/users"
              className="text-[11px] font-medium text-orange-600 hover:underline"
            >
              Ver todos
            </Link>
          }
        >
          {recent.users.length === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400">
              Nenhum cadastro recente.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recent.users.map((user) => (
                <li key={user.id}>
                  <Link
                    to={
                      user.companyId ? `/companies/${user.companyId}` : "/users"
                    }
                    className="group flex items-center justify-between gap-2 rounded-lg px-1 py-2 transition hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-slate-900 group-hover:text-orange-600">
                        {user.name}
                      </p>
                      <p className="truncate text-[10px] text-slate-400">
                        {user.company || "Sem empresa"} ·{" "}
                        {moment(user.createdAt).format("DD/MM/YYYY")}
                      </p>
                    </div>
                    <Badge color={user.isActive ? "success" : "failure"} size="xs">
                      {PERMISSION_LABELS[user.permissionId] || user.permissionId}
                    </Badge>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Últimos pagamentos"
          subtitle="Cobranças registradas no sistema"
          action={
            <Link
              to="/admin/payments"
              className="text-[11px] font-medium text-orange-600 hover:underline"
            >
              Ver todos
            </Link>
          }
        >
          {recent.payments.length === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400">
              Nenhum pagamento registrado.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recent.payments.map((payment) => (
                <li key={payment.id}>
                  <Link
                    to={`/companies/${payment.companyId}`}
                    className="group flex items-center justify-between gap-2 rounded-lg px-1 py-2 transition hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-slate-900 group-hover:text-orange-600">
                        {payment.company}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {payment.paymentMethod || "—"} ·{" "}
                        {moment(payment.paidAt || payment.createdAt).format(
                          "DD/MM/YYYY"
                        )}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-xs font-semibold text-slate-900">
                        {formatCurrency(payment.amount)}
                      </p>
                      <Badge
                        color={
                          payment.status === "APPROVED"
                            ? "success"
                            : payment.status === "PENDING"
                            ? "warning"
                            : "failure"
                        }
                        size="xs"
                      >
                        {payment.status}
                      </Badge>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Linha 6 — segmentos e vencimentos */}
      <div className="grid gap-4 xl:grid-cols-3">
        <Panel
          className="xl:col-span-2"
          title="Segmentos atendidos"
          subtitle="Distribuição das empresas por segmento de mercado"
        >
          {distribution.segments.length === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400">
              Nenhum segmento cadastrado.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {distribution.segments.slice(0, 10).map((segment) => (
                <ShareBar
                  key={segment.name}
                  label={segment.name}
                  count={segment.count}
                  percent={segment.percent}
                  color="bg-sky-500"
                />
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Agenda de vencimentos" subtitle="Renovações a acompanhar">
          <div className="space-y-2">
            {[
              ["Expiradas", distribution.expiration.expired, "failure", "expired"],
              ["Vencem em 7 dias", distribution.expiration.next7, "warning", "expiring"],
              ["Vencem em 15 dias", distribution.expiration.next15, "warning", "expiring"],
              ["Vencem em 30 dias", distribution.expiration.next30, "info", "expiring"],
              ["Sem data definida", distribution.expiration.none, "gray", "none"],
            ].map(([label, count, color, filter]) => (
              <Link
                key={label}
                to={`/companies?expirationStatus=${filter}`}
                className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2 transition hover:border-slate-200 hover:bg-slate-50"
              >
                <span className="text-xs text-slate-600">{label}</span>
                <Badge color={color} size="xs">
                  {formatNumber(count)}
                </Badge>
              </Link>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
